import type { Id } from "@repo/convex/dataModel";
import {
  Building2,
  Folder,
  FolderHeart,
  FolderOpen,
  LayoutTemplate,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  TrendingUp,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { InlineEditable } from "@/components/inline-editable";
import { OrgDropdown } from "@/components/org-dropdown";
import { SidebarResizeHandle } from "@/components/sidebar-resize-handle";
import { SidebarUserProfile } from "@/components/sidebar-user-profile";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { LongLabelTooltip } from "@/components/ui/long-label-tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { TreeChevron } from "@/components/ui/tree-chevron";
import { useOrganization } from "@/contexts/organization-context";
import {
  FolderContextMenu,
  FolderOverflowMenu,
} from "@/features/home/components/folder-context-menu";
import { useDropTarget } from "@/features/home/hooks/use-drop-target";
import {
  emitSelectedGroupChanged,
  folderGroupId,
  loadSelectedGroup,
  MY_PROJECTS_GROUP_ID,
  saveSelectedGroup,
} from "@/features/home/project-group-selection";
import {
  type GroupedProjectsResult,
  getValidFolderMoveTargets,
  type HomeProjectFolder,
} from "@/features/home/types";
import { useTreeKeyboard } from "@/features/project/components/tree";
import {
  useCreateFolder,
  useGroupedProjects,
  useMoveProject,
  useRenameFolder,
} from "@/features/project/hooks";
import { cn } from "@/lib/utils";

const FOLDER_EXPANSION_STORAGE_PREFIX = "df-home-folder-expansion:v1";

function getExpansionStorageKey(organizationId: string | null) {
  return `${FOLDER_EXPANSION_STORAGE_PREFIX}:${organizationId ?? "none"}`;
}

function loadExpandedMap(storageKey: string): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? (JSON.parse(stored) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveExpandedMap(storageKey: string, expandedMap: Record<string, boolean>) {
  localStorage.setItem(storageKey, JSON.stringify(expandedMap));
}

function collectFolderGroupIds(folders: HomeProjectFolder[]): string[] {
  const ids: string[] = [];
  for (const folderNode of folders) {
    const childNodes = folderNode.children ?? [];
    ids.push(folderGroupId(String(folderNode.folder._id)));
    ids.push(...collectFolderGroupIds(childNodes));
  }
  return ids;
}

function findFolderNodeByGroupId(
  folders: HomeProjectFolder[],
  groupId: string
): HomeProjectFolder | null {
  for (const folderNode of folders) {
    const childNodes = folderNode.children ?? [];
    if (folderGroupId(String(folderNode.folder._id)) === groupId) {
      return folderNode;
    }
    const nestedMatch = findFolderNodeByGroupId(childNodes, groupId);
    if (nestedMatch) {
      return nestedMatch;
    }
  }
  return null;
}

function InlineFolderDraftRow({
  depth,
  value,
  disabled,
  onChange,
  onSubmit,
  onCancel,
}: {
  depth: number;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-1.5 py-1" style={{ paddingLeft: `${28 + depth * 12}px` }}>
      <FolderOpen className="size-3.5 shrink-0 text-muted-foreground" />
      <Input
        autoFocus
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder="New folder"
        className="h-7 select-text text-[13px]"
        onBlur={() => {
          if (!disabled) {
            onCancel();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            onSubmit();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
      />
    </div>
  );
}

function FolderTreeNode({
  node,
  allFolders,
  selectedGroupId,
  expandedMap,
  onSelect,
  onToggle,
  draftParentGroupId,
  draftName,
  isSavingDraft,
  onDraftNameChange,
  onDraftSubmit,
  onDraftCancel,
  onProjectDrop,
  renamingFolderId,
  onRenameStart,
  onRenameClear,
  onRenameComplete,
  depth,
}: {
  node: HomeProjectFolder;
  allFolders: HomeProjectFolder[];
  selectedGroupId: string;
  expandedMap: Record<string, boolean>;
  onSelect: (groupId: string) => void;
  onToggle: (groupId: string, expanded: boolean) => void;
  draftParentGroupId: string | null | undefined;
  draftName: string;
  isSavingDraft: boolean;
  onDraftNameChange: (value: string) => void;
  onDraftSubmit: () => void;
  onDraftCancel: () => void;
  onProjectDrop: (projectId: Id<"projects">, folderId: Id<"projectFolders"> | undefined) => void;
  renamingFolderId: Id<"projectFolders"> | null;
  onRenameStart: (folderId: Id<"projectFolders">) => void;
  onRenameClear: () => void;
  onRenameComplete: (folderId: Id<"projectFolders">, newName: string) => void;
  depth: number;
}) {
  const groupId = folderGroupId(String(node.folder._id));
  const childNodes = node.children ?? [];
  const isDraftParent = draftParentGroupId === groupId;
  const hasChildren = childNodes.length > 0;
  const hasContent = hasChildren || isDraftParent;
  const expanded = isDraftParent ? true : (expandedMap[groupId] ?? true);
  const isSelected = selectedGroupId === groupId;

  const isRenaming = renamingFolderId === node.folder._id;
  const validMoveTargets = useMemo(
    () => getValidFolderMoveTargets(allFolders, node.folder._id),
    [allFolders, node.folder._id]
  );

  const { isOver, isValidTarget, isDragActive, dropTargetProps } = useDropTarget({
    folderId: node.folder._id,
    onDrop: onProjectDrop,
  });

  return (
    <Collapsible
      open={hasContent ? expanded : false}
      onOpenChange={(open) => {
        if (hasChildren) {
          onToggle(groupId, open);
        }
      }}
      role="treeitem"
      aria-expanded={hasContent ? expanded : undefined}
      aria-selected={isSelected}
      data-tree-node-id={groupId}
      tabIndex={-1}
      className="space-y-1"
    >
      <div className="group/folder flex items-center">
        <div className="relative flex flex-1 min-w-0">
          <FolderContextMenu
            folderId={node.folder._id}
            folderName={node.folder.name}
            currentParentFolderId={node.folder.parentFolderId}
            validMoveTargets={validMoveTargets}
            projectCount={node.projects.length}
            childFolderCount={childNodes.length}
            onRenameStart={() => onRenameStart(node.folder._id)}
            className="flex flex-1 min-w-0"
          >
            {(() => {
              // Indent so that the folder icon aligns with Library/Org items (pl-5 = 20px).
              // Chevron (16px slot) sits before the icon, eating into the padding.
              // depth=0: icon at 20px. depth=1: icon at 32px. etc.
              const iconIndent = 28 + depth * 12;
              const rowClassName = cn(
                "relative flex h-7 w-full select-none items-center gap-1.5 border-l-2 border-transparent pr-9 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/50 focus-visible:text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground data-[selected=true]:border-primary data-[selected=true]:font-medium touch:min-h-11",
                isDragActive && isValidTarget && "ring-1 ring-primary/30",
                isOver && isValidTarget && "ring-2 ring-primary bg-primary/10"
              );
              // Chevron is absolutely positioned 16px to the left of the icon
              const chevron = hasChildren ? (
                <CollapsibleTrigger
                  type="button"
                  data-tree-toggle="true"
                  tabIndex={-1}
                  className="absolute flex size-4 items-center justify-center text-neutral-dim"
                  style={{ left: iconIndent - 18 }}
                  aria-label={expanded ? "Collapse folder" : "Expand folder"}
                >
                  <TreeChevron open={expanded} />
                </CollapsibleTrigger>
              ) : null;
              const icon = isSelected ? (
                <FolderOpen className="size-3.5 shrink-0 text-primary" />
              ) : (
                <Folder className="size-3.5 shrink-0 text-neutral-dim" />
              );
              const editable = (
                <InlineEditable
                  value={node.folder.name}
                  isEditing={isRenaming}
                  onSave={(newName) => onRenameComplete(node.folder._id, newName)}
                  onCancel={onRenameClear}
                  className="flex-1"
                  inputClassName="select-text text-[13px]"
                />
              );
              const label = isRenaming ? (
                editable
              ) : (
                <LongLabelTooltip
                  content={node.folder.name}
                  side="right"
                  align="start"
                  contentClassName="max-w-80 whitespace-normal break-words"
                >
                  <span className="min-w-0 flex-1 truncate">{node.folder.name}</span>
                </LongLabelTooltip>
              );

              // When renaming, render a <div> to avoid invalid <input> inside <button> nesting
              if (isRenaming) {
                return (
                  <div
                    className={rowClassName}
                    style={{ paddingLeft: iconIndent }}
                    data-selected={isSelected}
                    {...dropTargetProps}
                  >
                    {chevron}
                    {icon}
                    {label}
                  </div>
                );
              }

              return (
                <button
                  type="button"
                  tabIndex={-1}
                  className={rowClassName}
                  style={{ paddingLeft: iconIndent }}
                  data-selected={isSelected}
                  onClick={() => onSelect(groupId)}
                  {...dropTargetProps}
                >
                  {chevron}
                  {icon}
                  {label}
                </button>
              );
            })()}
          </FolderContextMenu>
          {!isRenaming ? (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-center">
              <span className="px-1.5 py-0.5 text-xs text-neutral-dim transition-opacity group-hover/folder:opacity-0">
                {node.projects.length}
              </span>
              <div className="pointer-events-auto absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover/folder:opacity-100">
                <FolderOverflowMenu
                  folderId={node.folder._id}
                  folderName={node.folder.name}
                  currentParentFolderId={node.folder.parentFolderId}
                  validMoveTargets={validMoveTargets}
                  projectCount={node.projects.length}
                  childFolderCount={childNodes.length}
                  onRenameStart={() => onRenameStart(node.folder._id)}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {hasContent ? (
        <CollapsibleContent role="group" className="space-y-1">
          {isDraftParent ? (
            <InlineFolderDraftRow
              depth={depth + 1}
              value={draftName}
              disabled={isSavingDraft}
              onChange={onDraftNameChange}
              onSubmit={onDraftSubmit}
              onCancel={onDraftCancel}
            />
          ) : null}
          {childNodes.map((childNode) => (
            <FolderTreeNode
              key={childNode.folder._id}
              node={childNode}
              allFolders={allFolders}
              selectedGroupId={selectedGroupId}
              expandedMap={expandedMap}
              onSelect={onSelect}
              onToggle={onToggle}
              draftParentGroupId={draftParentGroupId}
              draftName={draftName}
              isSavingDraft={isSavingDraft}
              onDraftNameChange={onDraftNameChange}
              onDraftSubmit={onDraftSubmit}
              onDraftCancel={onDraftCancel}
              onProjectDrop={onProjectDrop}
              renamingFolderId={renamingFolderId}
              onRenameStart={onRenameStart}
              onRenameClear={onRenameClear}
              onRenameComplete={onRenameComplete}
              depth={depth + 1}
            />
          ))}
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
}

/**
 * LeftSidebar - Navigation sidebar for non-project routes.
 */
export function LeftSidebar() {
  const { organizationId } = useOrganization();
  const { toggleSidebar, open } = useSidebar();
  const groupedProjects = useGroupedProjects(organizationId) as GroupedProjectsResult | undefined;
  const createFolder = useCreateFolder();
  const moveProject = useMoveProject();
  const renameFolder = useRenameFolder();

  const handleProjectDrop = useCallback(
    async (projectId: Id<"projects">, folderId: Id<"projectFolders"> | undefined) => {
      try {
        await moveProject({ projectId, folderId });
        toast.success(folderId ? "Project moved to folder" : "Project moved to My Projects");
      } catch {
        toast.error("Failed to move project");
      }
    },
    [moveProject]
  );

  const {
    isOver: isOverMyProjects,
    isValidTarget: isMyProjectsValid,
    isDragActive,
    dropTargetProps: myProjectsDropProps,
  } = useDropTarget({ folderId: undefined, onDrop: handleProjectDrop });

  const organizationIdString = organizationId ? String(organizationId) : null;
  const expansionStorageKey = useMemo(
    () => getExpansionStorageKey(organizationIdString),
    [organizationIdString]
  );

  const [selectedGroupId, setSelectedGroupId] = useState<string>(() =>
    loadSelectedGroup(organizationIdString)
  );
  const [pendingCreatedFolderGroupId, setPendingCreatedFolderGroupId] = useState<string | null>(
    null
  );
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>(() =>
    loadExpandedMap(expansionStorageKey)
  );
  const [draftFolderParentGroupId, setDraftFolderParentGroupId] = useState<
    string | null | undefined
  >(undefined);
  const [draftFolderName, setDraftFolderName] = useState("");
  const [isSavingFolder, setIsSavingFolder] = useState(false);
  const [renamingFolderId, setRenamingFolderId] = useState<Id<"projectFolders"> | null>(null);

  // Section collapse state
  const [projectsSectionOpen, setProjectsSectionOpen] = useState(true);
  const [librarySectionOpen, setLibrarySectionOpen] = useState(true);

  const handleFolderRenameComplete = useCallback(
    async (folderId: Id<"projectFolders">, newName: string) => {
      setRenamingFolderId(null);
      try {
        await renameFolder({ folderId, name: newName });
        toast.success("Folder renamed");
      } catch {
        toast.error("Failed to rename folder");
      }
    },
    [renameFolder]
  );

  const projectsTreeRef = useTreeKeyboard({
    expandNode: (nodeId) => setExpandedMap((prev) => ({ ...prev, [nodeId]: true })),
    collapseNode: (nodeId) => setExpandedMap((prev) => ({ ...prev, [nodeId]: false })),
  });

  useEffect(() => {
    setSelectedGroupId(loadSelectedGroup(organizationIdString));
    setPendingCreatedFolderGroupId(null);
    setExpandedMap(loadExpandedMap(expansionStorageKey));
    setDraftFolderParentGroupId(undefined);
    setDraftFolderName("");
    setRenamingFolderId(null);
  }, [organizationIdString, expansionStorageKey]);

  useEffect(() => {
    saveSelectedGroup(organizationIdString, selectedGroupId);
    emitSelectedGroupChanged(selectedGroupId);
  }, [organizationIdString, selectedGroupId]);

  useEffect(() => {
    saveExpandedMap(expansionStorageKey, expandedMap);
  }, [expansionStorageKey, expandedMap]);

  useEffect(() => {
    if (!groupedProjects) return;

    const validGroupIds = new Set<string>([
      MY_PROJECTS_GROUP_ID,
      ...collectFolderGroupIds(groupedProjects.folders),
    ]);

    if (validGroupIds.has(selectedGroupId) && pendingCreatedFolderGroupId === selectedGroupId) {
      setPendingCreatedFolderGroupId(null);
      return;
    }

    if (!validGroupIds.has(selectedGroupId) && pendingCreatedFolderGroupId === selectedGroupId) {
      return;
    }

    if (!validGroupIds.has(selectedGroupId)) {
      setSelectedGroupId(MY_PROJECTS_GROUP_ID);
    }
  }, [groupedProjects, selectedGroupId, pendingCreatedFolderGroupId]);

  const handleCancelDraftFolder = () => {
    if (isSavingFolder) return;
    setDraftFolderParentGroupId(undefined);
    setDraftFolderName("");
  };

  const handleSubmitDraftFolder = async () => {
    if (!organizationId) return;

    const name = draftFolderName.trim();
    if (!name) {
      handleCancelDraftFolder();
      return;
    }

    setIsSavingFolder(true);
    try {
      const parentFolderNode =
        draftFolderParentGroupId && groupedProjects
          ? findFolderNodeByGroupId(groupedProjects.folders, draftFolderParentGroupId)
          : null;

      const createArgs = parentFolderNode?.folder._id
        ? { orgId: organizationId, name, parentFolderId: parentFolderNode?.folder._id }
        : { orgId: organizationId, name };
      const newFolderId = await createFolder(createArgs);
      const groupId = folderGroupId(String(newFolderId));

      if (draftFolderParentGroupId) {
        setExpandedMap((prev) => ({ ...prev, [draftFolderParentGroupId]: true }));
      }

      setPendingCreatedFolderGroupId(groupId);
      setSelectedGroupId(groupId);
      setExpandedMap((prev) => ({ ...prev, [groupId]: true }));
      setDraftFolderName("");
      setDraftFolderParentGroupId(undefined);
      toast.success("Folder created");
    } catch {
      toast.error("Failed to create folder");
    } finally {
      setIsSavingFolder(false);
    }
  };

  const handleStartDraftFolder = () => {
    const parentGroupId = selectedGroupId.startsWith("folder:") ? selectedGroupId : null;
    if (parentGroupId) {
      setExpandedMap((prev) => ({ ...prev, [parentGroupId]: true }));
    }
    setDraftFolderParentGroupId(parentGroupId);
    setDraftFolderName("");
  };

  const CollapseIcon = open ? PanelLeftClose : PanelLeftOpen;

  const myProjectsIsSelected = selectedGroupId === MY_PROJECTS_GROUP_ID;

  return (
    <Sidebar side="left" collapsible="offcanvas" className="group/sidebar">
      <SidebarHeader className="gap-0 p-0">
        <div className="flex h-12 items-center justify-between px-4">
          <OrgDropdown />
          <button
            type="button"
            onClick={toggleSidebar}
            aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
            aria-expanded={open}
            className="flex size-4 items-center justify-center text-neutral-dim opacity-0 transition-opacity no-touch:group-hover/sidebar:opacity-100 touch:opacity-100"
          >
            <CollapseIcon className="size-4" />
          </button>
        </div>
        <div className="h-px bg-border" />
      </SidebarHeader>

      <SidebarContent>
        {/* Projects section — collapsible */}
        <SidebarGroup className="py-1">
          <Collapsible open={projectsSectionOpen} onOpenChange={setProjectsSectionOpen}>
            <div className="flex items-center justify-between px-2">
              <CollapsibleTrigger className="flex select-none items-center gap-1.5 px-1 py-1 text-[13px] font-medium text-muted-foreground hover:text-foreground">
                <TreeChevron open={projectsSectionOpen} className="text-neutral-dim" />
                <span>Projects</span>
              </CollapsibleTrigger>
              <button
                type="button"
                className="select-none px-1 text-xs font-medium text-neutral-dim hover:text-foreground"
                onClick={handleStartDraftFolder}
                disabled={groupedProjects === undefined || isSavingFolder}
              >
                + New Folder
              </button>
            </div>

            <CollapsibleContent>
              {groupedProjects === undefined ? (
                <div className="px-2 py-3 text-xs text-muted-foreground">Loading folders...</div>
              ) : (
                <div ref={projectsTreeRef} role="tree" aria-label="Projects" className="space-y-px">
                  {/* My Projects — same pattern as FolderTreeNode but no children/chevron */}
                  <button
                    type="button"
                    role="treeitem"
                    data-tree-node-id={MY_PROJECTS_GROUP_ID}
                    tabIndex={-1}
                    aria-selected={myProjectsIsSelected}
                    className={cn(
                      "relative flex h-7 w-full select-none items-center gap-1.5 border-l-2 border-transparent pr-9 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:bg-muted/50 focus-visible:text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground data-[selected=true]:border-primary data-[selected=true]:font-medium touch:min-h-11",
                      isDragActive && isMyProjectsValid && "ring-1 ring-primary/30",
                      isOverMyProjects && isMyProjectsValid && "ring-2 ring-primary bg-primary/10"
                    )}
                    style={{ paddingLeft: 28 }}
                    data-selected={myProjectsIsSelected}
                    onClick={() => setSelectedGroupId(MY_PROJECTS_GROUP_ID)}
                    {...myProjectsDropProps}
                  >
                    <FolderHeart
                      className={cn(
                        "size-3.5 shrink-0",
                        myProjectsIsSelected ? "text-primary" : "text-neutral-dim"
                      )}
                    />
                    <span className="flex-1 truncate">My Projects</span>
                    <span className="absolute inset-y-0 right-0 flex w-8 items-center justify-center">
                      <span className="px-1.5 py-0.5 text-xs text-neutral-dim">
                        {groupedProjects.unfiledProjects.length}
                      </span>
                    </span>
                  </button>

                  {draftFolderParentGroupId === null ? (
                    <InlineFolderDraftRow
                      depth={0}
                      value={draftFolderName}
                      disabled={isSavingFolder}
                      onChange={setDraftFolderName}
                      onSubmit={() => void handleSubmitDraftFolder()}
                      onCancel={handleCancelDraftFolder}
                    />
                  ) : null}

                  {groupedProjects.folders.map((folderNode) => (
                    <FolderTreeNode
                      key={folderNode.folder._id}
                      node={folderNode}
                      allFolders={groupedProjects.folders}
                      selectedGroupId={selectedGroupId}
                      expandedMap={expandedMap}
                      onSelect={setSelectedGroupId}
                      onToggle={(groupId, expanded) =>
                        setExpandedMap((prev) => ({ ...prev, [groupId]: expanded }))
                      }
                      draftParentGroupId={draftFolderParentGroupId}
                      draftName={draftFolderName}
                      isSavingDraft={isSavingFolder}
                      onDraftNameChange={setDraftFolderName}
                      onDraftSubmit={() => void handleSubmitDraftFolder()}
                      onDraftCancel={handleCancelDraftFolder}
                      onProjectDrop={handleProjectDrop}
                      renamingFolderId={renamingFolderId}
                      onRenameStart={setRenamingFolderId}
                      onRenameClear={() => setRenamingFolderId(null)}
                      onRenameComplete={handleFolderRenameComplete}
                      depth={0}
                    />
                  ))}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        <div className="mx-4 h-px bg-border" />

        {/* Library section — collapsible */}
        <SidebarGroup className="py-1">
          <Collapsible open={librarySectionOpen} onOpenChange={setLibrarySectionOpen}>
            <div className="px-2">
              <CollapsibleTrigger className="flex select-none items-center gap-1.5 px-1 py-1 text-[13px] font-medium text-muted-foreground hover:text-foreground">
                <TreeChevron open={librarySectionOpen} className="text-neutral-dim" />
                <span>Library</span>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="space-y-px px-2">
                <button
                  type="button"
                  className="flex h-7 w-full select-none items-center gap-1.5 px-2 pl-5 text-left text-[13px] text-muted-foreground hover:bg-muted/50 hover:text-foreground touch:min-h-11"
                >
                  <LayoutTemplate className="size-3.5 shrink-0 text-neutral-dim" />
                  <span>Templates</span>
                </button>
                <button
                  type="button"
                  className="flex h-7 w-full select-none items-center gap-1.5 px-2 pl-5 text-left text-[13px] text-muted-foreground hover:bg-muted/50 hover:text-foreground touch:min-h-11"
                >
                  <TrendingUp className="size-3.5 shrink-0 text-neutral-dim" />
                  <span>Shared Metrics</span>
                </button>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        <div className="mx-4 h-px bg-border" />

        {/* Organization section — not collapsible */}
        <SidebarGroup className="py-1">
          <div className="px-2">
            <div className="flex select-none items-center gap-1.5 px-1 py-1 text-[13px] font-medium text-muted-foreground">
              <Building2 className="size-3.5 text-neutral-dim" />
              <span>Organization</span>
            </div>
          </div>
          <div className="space-y-px px-2">
            <button
              type="button"
              className="flex h-7 w-full select-none items-center gap-1.5 px-2 pl-5 text-left text-[13px] text-muted-foreground hover:bg-muted/50 hover:text-foreground touch:min-h-11"
            >
              <Settings className="size-3.5 shrink-0 text-neutral-dim" />
              <span>Team Settings</span>
            </button>
            <button
              type="button"
              className="flex h-7 w-full select-none items-center gap-1.5 px-2 pl-5 text-left text-[13px] text-muted-foreground hover:bg-muted/50 hover:text-foreground touch:min-h-11"
            >
              <Users className="size-3.5 shrink-0 text-neutral-dim" />
              <span>Members</span>
            </button>
          </div>
        </SidebarGroup>
      </SidebarContent>

      <SidebarUserProfile />
      <SidebarResizeHandle />
    </Sidebar>
  );
}
